import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * A picture of the product with numbered markers on it, and the same numbers explained beneath.
 *
 * ## Why numbers and not arrows drawn into the image
 *
 * An arrow burned into a screenshot cannot be translated, cannot be read by a screen reader, and
 * has to be redrawn the day a button moves. A marker positioned in percentages sits on top of the
 * image, moves with it at any width, and its explanation is ordinary text in the page.
 *
 * ## The number sits beside the control, never on it
 *
 * The first version centred each number on its target, which put a filled circle squarely over the
 * label of every button it was pointing at — the reader could see something was marked and could
 * no longer read what it was. So a callout is a leader line whose tip rests on the point and a
 * number at the far end of it. `place` says which side the number goes, and it is chosen per
 * callout against the real screenshot, because the free space is on a different side every time.
 *
 * There was a dot on the point as well for one revision. It read as a second, competing mark on a
 * figure that already had a line and a number saying the same thing, and on a dense screenshot a
 * page of them looked like a rash. The line's tip is the pointer.
 *
 * ## Restraint is part of the format
 *
 * Every marker costs the reader a glance, so a figure earns markers only for what the step is
 * about. Labelling every control on a screen produces a picture nobody reads and a legend nobody
 * finishes; the surrounding prose is the cheaper place for everything else. Some figures are
 * better with no markers at all — pass an empty `callouts` and the picture is shown plain.
 *
 * ## Positions are percentages on purpose
 *
 * The same figure is read on a phone and on a 4K monitor. Pixel offsets would land the marker on
 * the wrong control at every width except the one it was authored at. The *offsets* below are in
 * pixels rather than percentages for the opposite reason: they are a distance on the reader's
 * screen, not a distance in the screenshot, so a thumb-sized gap stays thumb-sized.
 *
 * ## On a phone the picture scrolls rather than shrinks
 *
 * A 1900px dashboard scaled into a 325px column is not a small screenshot, it is an unreadable
 * one, and the markers were colliding with each other on top of it — six of them on a figure
 * 159px tall. Below `sm` the image keeps a 600px floor and scrolls sideways inside its own box,
 * which is the only arrangement where both the screenshot and its numbers stay legible. The page
 * itself never scrolls horizontally.
 */

/** Which side of the point the number sits on. */
export type CalloutPlacement = 'left' | 'right' | 'above' | 'below';

export interface Callout {
  /** Distance from the left of the image, 0–100. */
  x: number;
  /** Distance from the top of the image, 0–100. */
  y: number;
  /** What to do here. One instruction, imperative. */
  text: string;
  /**
   * Where the number goes relative to the point.
   *
   * Defaults to the side with more image on it, which is right often enough to be a sensible
   * default and wrong often enough that most callouts set it.
   */
  place?: CalloutPlacement;
}

/**
 * Marker and leader geometry, per side.
 *
 * ## The anchor edge is the point, not the box origin
 *
 * A `translate` moves a box by its own origin, and for an absolutely positioned element that origin
 * is the top-left corner -- so `left-0 -translate-x-[24px]` puts the marker's *left* edge 24px to
 * the left of the point, leaving its right edge only 24px minus its own width away. Measured on the
 * real page that came out at 12px, close enough to keep clipping the labels this whole mechanism
 * exists to keep clear, and close enough to hide the leader line underneath the circle.
 *
 * So each side anchors the edge that faces the point: `right-0` for a marker on the left,
 * `bottom-0` for one above. The marker is 18px across (20 from `sm`), its near edge lands at
 * exactly 18px (20) from the point whatever its size, and the leader spans that whole gap — from
 * the point itself to the circle it meets.
 */
const PLACEMENT: Record<CalloutPlacement, { marker: string; leader: string }> = {
  left: {
    marker: 'top-0 right-0 -translate-x-[18px] -translate-y-1/2 sm:-translate-x-[20px]',
    leader: 'top-0 right-0 h-px w-[18px] -translate-y-1/2 sm:w-[20px]',
  },
  right: {
    marker: 'top-0 left-0 translate-x-[18px] -translate-y-1/2 sm:translate-x-[20px]',
    leader: 'top-0 left-0 h-px w-[18px] -translate-y-1/2 sm:w-[20px]',
  },
  above: {
    marker: 'bottom-0 left-0 -translate-x-1/2 -translate-y-[18px] sm:-translate-y-[20px]',
    leader: 'bottom-0 left-0 h-[18px] w-px -translate-x-1/2 sm:h-[20px]',
  },
  below: {
    marker: 'top-0 left-0 -translate-x-1/2 translate-y-[18px] sm:translate-y-[20px]',
    leader: 'top-0 left-0 h-[18px] w-px -translate-x-1/2 sm:h-[20px]',
  },
};

export function AnnotatedFigure({
  caption,
  callouts,
  children,
}: {
  caption: string;
  callouts: Callout[];
  /** The screenshot, or a drawn schematic of the screen. */
  children: ReactNode;
}) {
  return (
    <figure className="border-border/60 bg-card overflow-hidden rounded-xl border shadow-sm">
      <div className="overflow-x-auto">
        <div className="relative min-w-[600px] sm:min-w-0">
          {children}

          {callouts.map((callout, index) => {
            const geometry = PLACEMENT[callout.place ?? (callout.x > 50 ? 'left' : 'right')];

            return (
              // Zero-sized and positioned at the point, so the three pieces below can be offset
              // from it without any of them needing to know the image's size.
              <span
                key={callout.text}
                className="absolute"
                style={{ left: `${callout.x}%`, top: `${callout.y}%` }}
                aria-hidden
              >
                {/* The tip rests on the point, so the line is the pointer. */}
                <span className={cn('bg-primary absolute', geometry.leader)} />

                <span
                  className={cn(
                    'bg-primary text-primary-foreground ring-background absolute flex size-4.5 items-center justify-center rounded-full text-[9px] font-semibold shadow-md ring-2 sm:size-5 sm:text-[10px]',
                    geometry.marker,
                  )}
                >
                  {index + 1}
                </span>
              </span>
            );
          })}
        </div>
      </div>

      <figcaption className="border-border/60 bg-muted/20 border-t px-5 py-4 sm:px-6 sm:py-5">
        <p className="text-sm font-medium">{caption}</p>
        <p className="text-muted-foreground/70 mt-1 text-xs sm:hidden">
          Scroll the picture sideways to see all of it.
        </p>

        {callouts.length > 0 && (
          <ol className="mt-4 space-y-2.5">
            {callouts.map((callout, index) => (
              <li key={callout.text} className="flex gap-3 text-sm leading-relaxed">
                <span className="bg-primary/10 text-primary mt-0.5 flex size-4.5 shrink-0 items-center justify-center rounded-full text-[9px] font-semibold">
                  {index + 1}
                </span>
                <span className="text-muted-foreground">{callout.text}</span>
              </li>
            ))}
          </ol>
        )}
      </figcaption>
    </figure>
  );
}
